// Admin copy of the web app's src/device/DeviceContext.jsx (card 540f21a4); keep the two in step.
// What this event-day device's job includes (card 540f21a4), read from
// GET /job/me when the app opens and every minute after, so a change Devin
// makes on the Roles screen shows up without re-pairing. Outside device mode
// everything is allowed here (the organizer's own checks apply as always).
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getMe } from './deviceApi';
import { forgetDevice, readDevice, saveDevice } from './deviceSession';

const REFRESH_MS = 60 * 1000;
const DeviceCtx = createContext({ device: null, me: null, ended: false, can: () => true });

export function DeviceProvider({ children, onEnded }) {
  const [device, setDevice] = useState(readDevice());
  const [me, setMe] = useState(null);
  const [ended, setEnded] = useState(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const d = readDevice();
    if (!d) return;
    try {
      const r = await getMe(d.token);
      // only if this is still the same device: another request may have
      // ended it while this one was out (MC review 56b15e4d R1-F2)
      const now = readDevice();
      if (!alive.current || !now || now.token !== d.token) return;
      setMe(r);
      // keep the names shown on screen current (role renamed, label changed)
      const next = { ...d, eventName: r.event?.name || d.eventName, roleName: r.device?.role?.name || d.roleName, label: r.device?.label ?? d.label };
      saveDevice(next);
      setDevice(next);
    } catch (e) {
      if (!alive.current) return;
      if (e.status === 401) {
        if (readDevice()?.token !== d.token) return;
        forgetDevice();
        setDevice(null);
        setEnded(true);
        if (onEnded) onEnded();
      }
      // offline or a server hiccup: keep what we had and try again later
    }
  }, [onEnded]);

  useEffect(() => {
    if (!device) return undefined;
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!device]);

  const value = useMemo(() => {
    const functions = new Set(me?.device?.functions || []);
    return {
      device,
      me,
      ended,
      // outside device mode: always; in device mode: only what the job has
      // (nothing until the first answer, so no control flashes up and goes)
      can: (...codes) => !device || codes.some((c) => functions.has(c)),
    };
  }, [device, me, ended]);

  return <DeviceCtx.Provider value={value}>{children}</DeviceCtx.Provider>;
}

export const useJobDevice = () => useContext(DeviceCtx);
export const useCan = () => useContext(DeviceCtx).can;
